/**
 * Rendu des animaux.
 *
 * Un `InstancedMesh` par espece: trente especes et trois cent cinquante betes
 * tiennent en une poignee d'appels de dessin, parce qu'un maillage dont
 * `count` vaut zero ne coute rien du tout a three.js. Les instances hors du champ
 * de la camera ne sont donc ni ecrites ni dessinees (voir `majCamera`), et dans
 * un biome donne on ne paie que les especes qu'on voit vraiment.
 *
 * Les membres ne bougent pas independamment: toute la DEMARCHE est portee par la
 * matrice d'instance (tangage, roulis, lacet, saut, souffle, ondulation de
 * l'echelle). C'est ce qui permet de tenir des centaines d'animaux, et c'est
 * largement suffisant a cette distance de camera pourvu qu'on exploite les six
 * degres de liberte a fond: le lapin bondit et se cabre, le cerf trotte, l'ours
 * roule d'une epaule sur l'autre, le serpent ondule, la tortue avance par
 * a-coups, l'aigle plane incline sans battre des ailes, le dauphin sort de l'eau
 * en arc, le crabe se deplace de cote, le manchot se dandine, le papillon bat
 * des ailes six fois par seconde.
 *
 * Couleur: le materiau multiplie la couleur d'instance par la couleur de
 * sommet. La couleur d'instance est la tonalite CLAIRE de l'individu
 * (`couleurs[0]` de la table est la reference), et les couleurs de sommets ne
 * peuvent donc qu'assombrir: c'est ainsi qu'on obtient un ventre clair, le
 * masque du renard, les taches du cerf, les rayures du poisson, tout en gardant
 * une variation individuelle de pelage.
 */
import * as THREE from 'three'
import type { Animal, AnimalKind } from '../../shared/types/monde.ts'
import { clamp } from '../../shared/sim/alea.ts'
import { CARTE_X, CARTE_Z } from '../../shared/constants.ts'
import { ANIMAUX, PROFILS } from '../../shared/data/animaux.ts'
import type { Demarche } from '../../shared/data/animaux.ts'
import type { Partie } from './geometrie.ts'
import { fusionner, tr } from './geometrie.ts'

/** Nombre maximum d'individus par espece. Le pool est alloue une fois. */
const MAX_PAR_ESPECE = 64

/** Marge de securite du tri par champ de vision, en metres. */
const MARGE_CHAMP = 7

/**
 * Especes assez grosses pour que leur ombre compte. Les autres n'entrent pas
 * dans la passe d'ombre: une ombre de papillon ne se voit pas, et chaque
 * maillage qui projette une ombre est un appel de dessin de plus.
 */
const PORTE_OMBRE: readonly AnimalKind[] = [
  'ours',
  'cerf',
  'renne',
  'chameau',
  'panthere',
  'loup',
  'sanglier',
  'chevre',
  'singe',
  'renard',
]

// --------------------------------------------------------------- couleurs

const cTmp1 = new THREE.Color()
const cTmp2 = new THREE.Color()

/**
 * Couleur de sommet a poser pour obtenir `cible` quand l'instance porte `ref`.
 * Le produit se fait en espace lineaire dans le nuanceur: on divise donc en
 * lineaire, et on borne a 1 (on ne peut pas eclaircir la reference).
 */
function rel(cible: number, ref: number): number {
  cTmp1.setHex(cible)
  cTmp2.setHex(ref)
  const r = cTmp2.r > 1e-4 ? Math.min(1, cTmp1.r / cTmp2.r) : 1
  const v = cTmp2.g > 1e-4 ? Math.min(1, cTmp1.g / cTmp2.g) : 1
  const b = cTmp2.b > 1e-4 ? Math.min(1, cTmp1.b / cTmp2.b) : 1
  cTmp1.setRGB(r, v, b)
  return cTmp1.getHex()
}

// --------------------------------------------------------------- primitives

const sph = (r: number, w = 8, h = 6): THREE.BufferGeometry => new THREE.SphereGeometry(r, w, h)
const cyl = (haut: number, bas: number, h: number, s = 6): THREE.BufferGeometry =>
  new THREE.CylinderGeometry(haut, bas, h, s)
const con = (r: number, h: number, s = 6): THREE.BufferGeometry => new THREE.ConeGeometry(r, h, s)
const boite = (x: number, y: number, z: number): THREE.BufferGeometry => new THREE.BoxGeometry(x, y, z)

type V3 = [number, number, number]

/** Petit atelier: on empile des primitives en couleurs ABSOLUES. */
interface Atelier {
  p(geo: THREE.BufferGeometry, couleur: number, pos?: V3, rot?: V3, ech?: V3): void
  /** Meme piece a gauche et a droite. `x` est la position du cote droit. */
  paire(geo: () => THREE.BufferGeometry, couleur: number, pos: V3, rot?: V3, ech?: V3): void
  fin(): THREE.BufferGeometry
}

function atelier(ref: number): Atelier {
  const parties: Partie[] = []
  const a: Atelier = {
    p(geo, couleur, pos, rot, ech) {
      parties.push({ geo, couleur: rel(couleur, ref), transforme: tr(pos, rot, ech) })
    },
    paire(geo, couleur, pos, rot, ech) {
      a.p(geo(), couleur, pos, rot, ech)
      a.p(
        geo(),
        couleur,
        [-pos[0], pos[1], pos[2]],
        rot ? [rot[0], -rot[1], -rot[2]] : undefined,
        ech,
      )
    },
    fin() {
      return fusionner(parties)
    },
  }
  return a
}

// ------------------------------------------------------------- quadrupedes

interface OptQuad {
  ref: number
  /** Longueur du tronc, en metres. */
  corpsL: number
  /** Hauteur du centre du tronc au-dessus du sol. */
  corpsH: number
  /** Demi-largeur du tronc. */
  corpsR: number
  /** Surelevation des epaules par rapport a la croupe. */
  garrot: number
  /** Longueur du cou. */
  couL: number
  /** Redressement du cou: 0 a l'horizontale, 1 a la verticale. */
  couDroit: number
  teteR: number
  museauL: number
  oreille: 'pointue' | 'ronde' | 'longue' | 'aucune'
  cornes: 'aucune' | 'cornes' | 'ramure' | 'palmee' | 'defenses'
  queue: 'touffue' | 'fine' | 'courte' | 'aucune'
  /** Rayon des pattes. */
  patteR: number
  /** Bosse dorsale (ours, chameau, sanglier). */
  bosse: number
  corps: number
  ventre: number
  membres: number
  museau: number
  motif: 'aucun' | 'taches' | 'masque' | 'chaussettes' | 'soies'
  /** Couleur du motif. */
  tache: number
}

/**
 * Corps de quadrupede. Une seule fonction sert au renard, au cerf, au renne, au
 * sanglier, au loup, a la panthere, a l'ours, a la chevre et au chameau: ce qui
 * les distingue, ce sont les proportions, les oreilles, les cornes, la queue et
 * le motif — c'est-a-dire exactement ce qu'on reconnait d'un animal vu de dessus.
 */
function geoQuad(o: OptQuad): THREE.BufferGeometry {
  const a = atelier(o.ref)
  const yc = o.corpsH
  const R = o.corpsR
  const demiL = o.corpsL / 2

  // Croupe, flanc, poitrail: trois masses de largeur decroissante vers
  // l'arriere, pour un corps qui s'effile au lieu de faire un tube.
  a.p(sph(R * 0.88, 9, 7), o.corps, [0, yc, -demiL * 0.58], undefined, [1, 1.02, 1.05])
  a.p(sph(R * 0.97, 9, 7), o.corps, [0, yc + o.garrot * 0.35, -demiL * 0.08], undefined, [1, 0.98, (demiL * 0.8) / R])
  a.p(sph(R * 1.03, 9, 7), o.corps, [0, yc + o.garrot, demiL * 0.46], undefined, [1, 1, 1.1])
  a.p(sph(R * 0.82, 7, 5), o.ventre, [0, yc - R * 0.46, -demiL * 0.02], undefined, [1, 0.5, (demiL * 0.85) / R])
  if (o.bosse > 0) {
    a.p(sph(R * 0.9, 8, 6), o.corps, [0, yc + o.garrot + o.bosse * 0.5, demiL * 0.18], undefined, [0.9, o.bosse / (R * 0.9) + 0.5, 1.1])
  }

  // Cou et tete. Le cou part de l'epaule et monte selon `couDroit`.
  const zEp = demiL * 0.72
  const yEp = yc + o.garrot + R * 0.2
  const zT = zEp + o.couL * (1 - o.couDroit * 0.62)
  const yT = yEp + o.couL * o.couDroit
  a.p(
    cyl(R * 0.52, R * 0.72, o.couL * 1.06, 7),
    o.corps,
    [0, (yEp + yT) / 2, (zEp + zT) / 2],
    [Math.atan2(zT - zEp, yT - yEp), 0, 0],
  )
  a.p(sph(o.teteR, 9, 7), o.corps, [0, yT, zT], undefined, [0.92, 0.95, 1.15])
  // Museau, truffe, machoire.
  a.p(sph(o.teteR * 0.52, 7, 6), o.corps, [0, yT - o.teteR * 0.18, zT + o.museauL * 0.62], undefined, [0.8, 0.72, o.museauL / o.teteR * 1.1])
  a.p(sph(o.teteR * 0.26, 6, 5), o.museau, [0, yT - o.teteR * 0.14, zT + o.museauL * 1.18])
  a.paire(() => sph(o.teteR * 0.17, 5, 4), 0x14141a, [o.teteR * 0.5, yT + o.teteR * 0.28, zT + o.teteR * 0.6])

  // Oreilles.
  if (o.oreille !== 'aucune') {
    const h = o.oreille === 'longue' ? o.teteR * 1.5 : o.oreille === 'pointue' ? o.teteR * 0.95 : o.teteR * 0.6
    const l = o.oreille === 'ronde' ? o.teteR * 0.62 : o.teteR * 0.4
    if (o.oreille === 'ronde') {
      a.paire(() => sph(l, 7, 5), o.corps, [o.teteR * 0.72, yT + o.teteR * 0.72, zT - o.teteR * 0.15], undefined, [1, 1, 0.45])
    } else {
      a.paire(() => con(l, h, 5), o.corps, [o.teteR * 0.6, yT + o.teteR * 0.6 + h * 0.4, zT - o.teteR * 0.1], [-0.15, 0, 0.28])
      a.paire(() => con(l * 0.55, h * 0.7, 4), o.tache, [o.teteR * 0.6, yT + o.teteR * 0.58 + h * 0.38, zT - o.teteR * 0.02], [-0.15, 0, 0.28])
    }
  }

  // Cornes, ramures, defenses.
  if (o.cornes === 'cornes') {
    a.paire(() => cyl(o.teteR * 0.09, o.teteR * 0.2, o.teteR * 2.1, 5), o.membres, [o.teteR * 0.42, yT + o.teteR * 1.1, zT - o.teteR * 0.5], [0.65, 0, 0.22])
  } else if (o.cornes === 'ramure' || o.cornes === 'palmee') {
    // Ramure: un merrain incline vers l'arriere plus trois andouillers. Vue de
    // dessus, c'est ce qui distingue immediatement un cerf d'une chevre.
    const large = o.cornes === 'palmee'
    const perche = o.teteR * (large ? 3.1 : 3.4)
    for (const s of [-1, 1]) {
      a.p(
        cyl(o.teteR * 0.13, o.teteR * 0.22, perche, 5),
        o.membres,
        [s * o.teteR * 0.46 + s * perche * 0.16, yT + o.teteR * 0.72 + perche * 0.42, zT - o.teteR * 0.55],
        [-0.42, 0, s * -0.42],
      )
      for (let k = 0; k < 3; k++) {
        const t = 0.15 + k * 0.36
        a.p(
          cyl(o.teteR * 0.075, o.teteR * 0.12, o.teteR * (large ? 1.7 : 1.35), 4),
          o.membres,
          [
            s * (o.teteR * 0.46 + perche * (0.14 + 0.42 * t)),
            yT + o.teteR * 0.72 + perche * (0.3 + t * 0.75),
            zT - o.teteR * 0.55 + o.teteR * (0.9 - t * 1.7),
          ],
          [large ? -0.45 : -1.05, 0, s * (large ? -1.25 : -0.5)],
        )
      }
    }
  } else if (o.cornes === 'defenses') {
    a.paire(() => cyl(o.teteR * 0.045, o.teteR * 0.075, o.teteR * 0.55, 4), 0xf4efdf, [o.teteR * 0.3, yT - o.teteR * 0.2, zT + o.museauL * 0.95], [-0.7, 0, 0.35])
  }

  // Quatre pattes: une cuisse pleine, un canon plus fin, un pied pose au sol.
  const zPattes: [number, number] = [demiL * 0.6, -demiL * 0.58]
  for (const sz of zPattes) {
    const avant = sz > 0
    const yHaut = yc + (avant ? o.garrot : 0) - R * 0.2
    a.paire(() => sph(o.patteR * 1.75, 6, 5), o.corps, [R * 0.6, yHaut * 0.78, sz], undefined, [0.8, 1.15, 1.25])
    a.paire(
      () => cyl(o.patteR * 1.15, o.patteR * 0.68, yHaut, 6),
      o.membres,
      [R * 0.64, yHaut / 2, sz],
    )
    a.paire(
      () => sph(o.patteR * 1.05, 6, 5),
      o.motif === 'chaussettes' ? o.tache : o.membres,
      [R * 0.64, o.patteR * 0.6, sz],
      undefined,
      [1, 0.6, 1.4],
    )
  }

  // Queue.
  if (o.queue === 'touffue') {
    a.p(cyl(o.patteR * 0.7, o.patteR * 0.55, o.corpsL * 0.42, 5), o.corps, [0, yc + R * 0.3, -demiL * 0.9 - o.corpsL * 0.14], [1.05, 0, 0])
    a.p(sph(R * 0.46, 7, 5), o.corps, [0, yc + R * 0.08, -demiL * 0.9 - o.corpsL * 0.3], undefined, [1, 0.9, 1.5])
    a.p(sph(R * 0.32, 6, 5), o.ventre, [0, yc - R * 0.12, -demiL * 0.9 - o.corpsL * 0.46])
  } else if (o.queue === 'fine') {
    // Une chaine de billes: la queue retombe en courbe au lieu de faire un pieu.
    for (let k = 0; k < 5; k++) {
      const t = k / 4
      a.p(
        sph(o.patteR * (0.62 - t * 0.24), 5, 4),
        o.corps,
        [0, yc + R * 0.35 + Math.sin(t * 1.5) * o.corpsL * 0.16, -demiL * 0.92 - t * o.corpsL * 0.42],
      )
    }
  } else if (o.queue === 'courte') {
    a.p(sph(R * 0.24, 6, 5), o.ventre, [0, yc + R * 0.42, -demiL * 0.82], undefined, [1, 1, 0.8])
  }

  // Motifs: taches du faon, masque du renard, soies du sanglier.
  if (o.motif === 'taches') {
    const pts: [number, number, number][] = [
      [0.5, 0.55, 0.3],
      [-0.5, 0.6, 0.05],
      [0.55, 0.5, -0.25],
      [-0.45, 0.55, -0.5],
      [0.35, 0.75, 0.55],
      [-0.35, 0.75, -0.7],
    ]
    for (const [px, py, pz] of pts) {
      a.p(sph(R * 0.2, 5, 4), o.tache, [px * R, yc + py * R, pz * demiL], undefined, [1, 0.35, 1])
    }
  } else if (o.motif === 'masque') {
    a.paire(() => sph(o.teteR * 0.44, 6, 5), o.tache, [o.teteR * 0.55, yT + o.teteR * 0.1, zT + o.teteR * 0.35], undefined, [0.6, 1, 0.7])
  } else if (o.motif === 'soies') {
    for (let k = 0; k < 5; k++) {
      a.p(con(R * 0.1, R * 0.55, 4), o.tache, [0, yc + o.garrot * (1 - k * 0.14) + R * 0.95, demiL * (0.5 - k * 0.3)], [-0.25, 0, 0])
    }
  }

  return a.fin()
}

// -------------------------------------------------------------- rongeurs

interface OptRongeur {
  ref: number
  corps: number
  ventre: number
  taille: number
  oreilleH: number
  oreillePointe: number
  queue: 'boule' | 'panache' | 'courte'
  pattes: number
  tache: number
}

/** Lapin, lievre, ecureuil: assis sur les hanches, tete haute, oreilles nettes. */
function geoRongeur(o: OptRongeur): THREE.BufferGeometry {
  const a = atelier(o.ref)
  const s = o.taille
  // Hanches puissantes en arriere, poitrail plus bas, tete relevee.
  a.p(sph(0.19 * s, 9, 7), o.corps, [0, 0.2 * s, -0.06 * s], undefined, [1, 1.05, 1.2])
  a.p(sph(0.15 * s, 8, 6), o.corps, [0, 0.19 * s, 0.14 * s], undefined, [1, 0.95, 1.1])
  a.p(sph(0.13 * s, 7, 5), o.ventre, [0, 0.1 * s, 0.05 * s], undefined, [0.95, 0.6, 1.5])
  // Tete et museau.
  a.p(sph(0.115 * s, 9, 7), o.corps, [0, 0.33 * s, 0.24 * s], undefined, [0.95, 0.95, 1.1])
  a.p(sph(0.06 * s, 6, 5), o.corps, [0, 0.29 * s, 0.34 * s], undefined, [0.85, 0.8, 1.1])
  a.p(sph(0.028 * s, 5, 4), 0x8f5a63, [0, 0.3 * s, 0.4 * s])
  a.paire(() => sph(0.032 * s, 5, 4), 0x14141a, [0.075 * s, 0.36 * s, 0.29 * s])
  // Oreilles, longues et legerement ecartees.
  a.paire(
    () => con(0.042 * s, o.oreilleH * s, 5),
    o.corps,
    [0.055 * s, (0.4 + o.oreilleH * 0.42) * s, 0.19 * s],
    [-0.12, 0, 0.2],
  )
  a.paire(
    () => con(0.026 * s, o.oreilleH * 0.72 * s, 4),
    0xf0c8c8,
    [0.055 * s, (0.4 + o.oreilleH * 0.4) * s, 0.205 * s],
    [-0.12, 0, 0.2],
  )
  if (o.oreillePointe > 0) {
    a.paire(
      () => sph(0.036 * s, 5, 4),
      o.tache,
      [0.055 * s + o.oreilleH * 0.18 * s, (0.4 + o.oreilleH * 0.9) * s, 0.175 * s],
      undefined,
      [1, 1.2, 0.6],
    )
  }
  // Pattes: cuisses marquees a l'arriere, petites pattes avant.
  a.paire(() => sph(0.075 * s, 6, 5), o.corps, [0.125 * s, 0.12 * s, -0.1 * s], undefined, [0.75, 1, 1.4])
  a.paire(() => sph(0.05 * s, 5, 4), o.pattes, [0.115 * s, 0.035 * s, 0.02 * s], undefined, [0.8, 0.55, 1.7])
  a.paire(() => cyl(0.025 * s, 0.022 * s, 0.13 * s, 5), o.pattes, [0.095 * s, 0.07 * s, 0.19 * s], [0.35, 0, 0])
  // Queue.
  if (o.queue === 'boule') {
    a.p(sph(0.072 * s, 7, 6), o.ventre, [0, 0.19 * s, -0.26 * s])
  } else if (o.queue === 'panache') {
    // Le panache de l'ecureuil s'arque au-dessus du dos: c'est sa signature.
    for (let k = 0; k < 4; k++) {
      const t = k / 3
      a.p(
        sph((0.055 + t * 0.045) * s, 7, 5),
        k === 3 ? o.ventre : o.corps,
        [0, (0.2 + t * 0.3) * s, (-0.2 - t * 0.09) * s],
        undefined,
        [0.55, 1.15, 1],
      )
    }
  } else {
    a.p(sph(0.045 * s, 5, 4), o.ventre, [0, 0.2 * s, -0.24 * s])
  }
  return a.fin()
}

// -------------------------------------------------------------- oiseaux

interface OptOiseau {
  ref: number
  taille: number
  corps: number
  ventre: number
  dos: number
  bec: number
  becLong: number
  /** Crochu (rapace), plat (canard), pointu (passereau). */
  becForme: 'pointu' | 'crochu' | 'plat'
  /** Ailes deployees (planeur) ou repliees le long du corps. */
  ailes: 'deployees' | 'repliees' | 'nageoires'
  aileL: number
  /** Bout d'aile plus sombre. */
  bout: number
  /** Disque facial du hibou. */
  disque: boolean
  /** Aigrettes du hibou. */
  aigrettes: boolean
  pattes: number
  /** Longueur des pattes; 0 pour un oiseau posé sur l'eau. */
  patteH: number
  /** Redressement du corps: 0 horizontal, 1 debout (manchot). */
  debout: number
  queueL: number
}

function geoOiseau(o: OptOiseau): THREE.BufferGeometry {
  const a = atelier(o.ref)
  const s = o.taille
  const yc = (0.1 + o.patteH * 0.9 + o.debout * 0.16) * s
  const inc = o.debout * 1.35

  // Corps fuselé, incliné selon `debout`. Le plastron clair DEBORDE a l'avant:
  // concentrique, il restait invisible sous le dos et le manchot etait tout noir.
  a.p(sph(0.13 * s, 9, 7), o.dos, [0, yc, 0], [inc, 0, 0], [1, 0.95, 1.5])
  a.p(
    sph(0.108 * s, 8, 6),
    o.ventre,
    [0, yc - 0.03 * s - Math.sin(inc) * 0.02 * s, 0.055 * s * Math.cos(inc) + 0.02 * s],
    [inc, 0, 0],
    [0.94, 1.25, 0.95],
  )
  // Tete.
  const zT = 0.16 * s * Math.cos(inc)
  const yT = yc + 0.1 * s + Math.sin(inc) * 0.16 * s
  a.p(sph(0.085 * s, 8, 6), o.dos, [0, yT, zT], undefined, [1, 1, 1.05])
  if (o.disque) {
    a.p(sph(0.088 * s, 9, 6), o.ventre, [0, yT, zT + 0.03 * s], undefined, [1.05, 1.05, 0.5])
    a.paire(() => sph(0.032 * s, 7, 6), 0x3a2a18, [0.036 * s, yT + 0.008 * s, zT + 0.062 * s], undefined, [1, 1, 0.5])
    a.paire(() => sph(0.019 * s, 6, 5), 0x120e08, [0.036 * s, yT + 0.008 * s, zT + 0.072 * s])
  } else {
    a.paire(() => sph(0.021 * s, 5, 4), 0x14141a, [0.05 * s, yT + 0.02 * s, zT + 0.05 * s])
  }
  if (o.aigrettes) {
    a.paire(() => con(0.026 * s, 0.075 * s, 4), o.dos, [0.05 * s, yT + 0.1 * s, zT - 0.015 * s], [-0.15, 0, 0.35])
  }
  // Bec.
  if (o.becForme === 'plat') {
    a.p(sph(0.038 * s, 6, 5), o.bec, [0, yT - 0.012 * s, zT + o.becLong * s * 0.55], undefined, [1.1, 0.45, o.becLong * s / (0.038 * s) * 0.5])
  } else if (o.becForme === 'crochu') {
    a.p(con(0.034 * s, o.becLong * s, 5), o.bec, [0, yT - 0.005 * s, zT + o.becLong * s * 0.45], [1.35, 0, 0])
    a.p(sph(0.02 * s, 5, 4), o.bec, [0, yT - 0.035 * s, zT + o.becLong * s * 0.7], undefined, [0.8, 1.3, 0.7])
  } else {
    a.p(con(0.026 * s, o.becLong * s, 5), o.bec, [0, yT - 0.005 * s, zT + o.becLong * s * 0.5], [Math.PI / 2, 0, 0])
  }
  // Ailes.
  if (o.ailes === 'deployees') {
    a.paire(
      () => sph(o.aileL * s, 7, 4),
      o.dos,
      [o.aileL * s * 0.82, yc + 0.03 * s, -0.01 * s],
      [0, 0.22, -0.1],
      [1, 0.085, 0.42],
    )
    a.paire(
      () => sph(o.aileL * s * 0.34, 6, 4),
      o.bout,
      [o.aileL * s * 1.5, yc + 0.03 * s, -0.06 * s],
      [0, 0.4, -0.12],
      [1, 0.09, 0.55],
    )
  } else if (o.ailes === 'nageoires') {
    a.paire(
      () => sph(o.aileL * s, 6, 4),
      o.dos,
      [0.105 * s, yc + 0.01 * s, 0],
      [0, 0, 0.35],
      [0.28, 1, 0.42],
    )
  } else {
    a.paire(
      () => sph(o.aileL * s, 7, 5),
      o.dos,
      [0.1 * s, yc + 0.01 * s, -0.01 * s],
      [0, 0.12, 0.22],
      [0.3, 0.62, 1.25],
    )
    a.paire(
      () => sph(o.aileL * s * 0.5, 5, 4),
      o.bout,
      [0.095 * s, yc - 0.01 * s, -0.11 * s],
      [0, 0.2, 0.2],
      [0.32, 0.5, 1.2],
    )
  }
  // Queue en eventail.
  a.p(
    con(0.062 * s, o.queueL * s, 4),
    o.dos,
    [0, yc + 0.03 * s - Math.sin(inc) * 0.14 * s, -0.14 * s - o.queueL * s * 0.35],
    [-1.35 + inc * 0.6, 0, 0],
    [1.35, 1, 0.28],
  )
  // Pattes.
  if (o.patteH > 0) {
    a.paire(() => cyl(0.015 * s, 0.013 * s, o.patteH * s, 5), o.bec, [0.045 * s, o.patteH * s * 0.5, 0.01 * s])
    a.paire(() => boite(0.05 * s, 0.014 * s, 0.075 * s), o.bec, [0.045 * s, 0.008 * s, 0.035 * s])
  }
  return a.fin()
}

// -------------------------------------------------------------- nageurs

interface OptNageur {
  ref: number
  longueur: number
  rayon: number
  dos: number
  ventre: number
  /** Nageoire caudale horizontale (cetace) ou verticale (poisson). */
  caudale: 'horizontale' | 'verticale'
  dorsale: number
  /** Longueur du rostre (dauphin) ou du museau. */
  rostre: number
  /** Moustaches et grosse tete ronde (phoque). */
  ronde: boolean
  /** Rayures verticales (poisson). */
  rayures: number
  tache: number
}

function geoNageur(o: OptNageur): THREE.BufferGeometry {
  const a = atelier(o.ref)
  const L = o.longueur
  const R = o.rayon
  // Corps fuselé: quatre sections qui s'affinent vers la queue.
  const sections: [number, number][] = [
    [0.34, 0.82],
    [0.1, 1],
    [-0.18, 0.86],
    [-0.42, 0.5],
  ]
  for (const [t, k] of sections) {
    a.p(sph(R * k, 9, 6), o.dos, [0, 0, t * L], undefined, [1, 1, 1.5])
  }
  a.p(sph(R * 0.78, 8, 5), o.ventre, [0, -R * 0.42, 0], undefined, [0.9, 0.5, L / R * 0.34])
  // Tete.
  if (o.ronde) {
    a.p(sph(R * 0.95, 9, 7), o.dos, [0, R * 0.06, L * 0.44])
    a.p(sph(R * 0.42, 6, 5), o.dos, [0, -R * 0.15, L * 0.5 + R * 0.5], undefined, [0.9, 0.75, 0.8])
    a.paire(() => sph(R * 0.09, 5, 4), 0x2a2620, [R * 0.3, -R * 0.16, L * 0.5 + R * 0.62], undefined, [1, 1, 2.2])
  } else if (o.rostre > 0) {
    a.p(sph(R * 0.86, 8, 6), o.dos, [0, R * 0.05, L * 0.4])
    a.p(cyl(R * 0.17, R * 0.4, o.rostre, 6), o.dos, [0, -R * 0.05, L * 0.42 + o.rostre * 0.5], [Math.PI / 2, 0, 0])
    // Le sourire du dauphin.
    a.p(boite(R * 0.5, R * 0.05, o.rostre * 0.7), o.tache, [0, -R * 0.16, L * 0.42 + o.rostre * 0.35])
  } else {
    a.p(sph(R * 0.8, 8, 6), o.dos, [0, 0, L * 0.4], undefined, [0.95, 1, 1.2])
    a.p(con(R * 0.4, R * 0.6, 5), o.dos, [0, 0, L * 0.46 + R * 0.3], [Math.PI / 2, 0, 0])
  }
  a.paire(() => sph(R * 0.13, 5, 4), 0x14141a, [R * 0.52, R * 0.2, L * 0.42])
  // Nageoire dorsale.
  if (o.dorsale > 0) {
    a.p(con(o.dorsale, o.dorsale * 1.7, 4), o.dos, [0, R * 0.9 + o.dorsale * 0.6, L * 0.02], [-0.45, 0, 0], [0.28, 1, 1])
  }
  // Pectorales.
  a.paire(
    () => sph(R * 0.75, 6, 4),
    o.dos,
    [R * 0.72, -R * 0.28, L * 0.16],
    [0, -0.35, -0.55],
    [1, 0.11, 0.5],
  )
  // Caudale.
  if (o.caudale === 'horizontale') {
    a.paire(
      () => sph(R * 0.95, 6, 4),
      o.dos,
      [R * 0.7, 0, -L * 0.52],
      [0, 0.5, 0],
      [1, 0.1, 0.55],
    )
  } else {
    a.p(con(R * 1.5, R * 1.5, 4), o.dos, [0, 0, -L * 0.55], [Math.PI / 2, 0, 0], [1, 0.14, 1])
    a.p(con(R * 1.1, R * 1.2, 4), o.dos, [0, R * 0.75, -L * 0.6], [0.5, 0, 0], [0.14, 1, 1])
  }
  // Rayures.
  for (let k = 0; k < o.rayures; k++) {
    const t = 0.28 - k * (0.5 / Math.max(1, o.rayures))
    a.p(sph(R * 1.02, 8, 5), o.tache, [0, 0, t * L], undefined, [1, 1, 0.09])
  }
  return a.fin()
}

// ------------------------------------------------------- especes particulieres

function geoCrabe(): THREE.BufferGeometry {
  const ref = ANIMAUX.crabe.couleurs[0] as number
  const a = atelier(ref)
  const corps = 0xd8542a
  const clair = 0xffb894
  a.p(sph(0.2, 10, 6), corps, [0, 0.12, 0], undefined, [1.3, 0.5, 0.95])
  a.p(sph(0.17, 8, 5), clair, [0, 0.08, 0.02], undefined, [1.25, 0.3, 0.85])
  // Yeux sur pedoncules.
  a.paire(() => cyl(0.014, 0.014, 0.07, 4), corps, [0.07, 0.19, 0.1])
  a.paire(() => sph(0.028, 6, 5), 0x171720, [0.07, 0.23, 0.11])
  // Deux pinces, tendues vers l'avant.
  for (const s of [-1, 1]) {
    a.p(cyl(0.028, 0.024, 0.14, 5), corps, [s * 0.21, 0.11, 0.13], [0, s * -0.5, s * 0.5])
    a.p(sph(0.072, 7, 5), corps, [s * 0.29, 0.115, 0.23], [0, s * 0.5, 0], [1, 0.62, 1.5])
    a.p(boite(0.03, 0.02, 0.1), clair, [s * 0.31, 0.13, 0.29], [0, s * 0.5, 0])
    // Trois pattes de chaque cote, articulees.
    for (let i = 0; i < 3; i++) {
      const z = 0.02 - i * 0.095
      a.p(cyl(0.017, 0.013, 0.13, 4), corps, [s * 0.2, 0.1, z], [0, 0, s * 1.0])
      a.p(cyl(0.013, 0.009, 0.1, 4), corps, [s * 0.29, 0.045, z], [0, 0, s * 2.0])
    }
  }
  return a.fin()
}

function geoScorpion(): THREE.BufferGeometry {
  const ref = ANIMAUX.scorpion.couleurs[0] as number
  const a = atelier(ref)
  const corps = 0x8a6238
  const clair = 0xdcbf8e
  a.p(sph(0.1, 8, 5), corps, [0, 0.055, -0.02], undefined, [1.05, 0.45, 1.5])
  a.p(sph(0.07, 7, 5), corps, [0, 0.055, 0.14], undefined, [1.1, 0.5, 1])
  a.paire(() => sph(0.014, 5, 4), 0x100c08, [0.03, 0.08, 0.16])
  // Pinces en avant, coudees.
  for (const s of [-1, 1]) {
    a.p(cyl(0.018, 0.015, 0.11, 4), corps, [s * 0.08, 0.05, 0.2], [0, s * -0.55, 0])
    a.p(sph(0.052, 6, 5), corps, [s * 0.12, 0.05, 0.29], [0, s * -0.3, 0], [0.75, 0.55, 1.5])
    a.p(boite(0.02, 0.016, 0.07), clair, [s * 0.13, 0.055, 0.35], [0, s * -0.3, 0])
    // Quatre pattes.
    for (let i = 0; i < 4; i++) {
      const z = 0.08 - i * 0.06
      a.p(cyl(0.011, 0.008, 0.11, 4), corps, [s * 0.09, 0.04, z], [0, s * (0.5 - i * 0.35), s * 1.15])
    }
  }
  // Queue segmentee, arquee par-dessus le dos, dard au bout.
  for (let k = 0; k < 5; k++) {
    const t = k / 4
    a.p(
      sph(0.032 - t * 0.008, 6, 5),
      corps,
      [0, 0.06 + Math.sin(t * 1.5) * 0.17, -0.11 - t * 0.03 + Math.sin(t * 3) * 0.02],
    )
  }
  a.p(con(0.026, 0.07, 5), clair, [0, 0.25, -0.06], [0.9, 0, 0])
  return a.fin()
}

function geoSerpent(): THREE.BufferGeometry {
  const ref = ANIMAUX.serpent.couleurs[0] as number
  const a = atelier(ref)
  const dos = 0xb08c48
  const tache = 0x6d5228
  const ventre = 0xe8d69a
  // Corps en S: onze anneaux qui s'affinent.
  const N = 11
  for (let k = 0; k < N; k++) {
    const t = k / (N - 1)
    const r = 0.062 * (1 - t * 0.62)
    const z = 0.42 - t * 0.95
    const x = Math.sin(t * Math.PI * 2.1) * 0.13
    a.p(sph(r, 7, 5), k % 2 === 0 ? dos : tache, [x, r * 0.85, z], undefined, [1, 0.78, 1.25])
  }
  // Tete en coin, plus large que le cou.
  a.p(sph(0.062, 8, 6), dos, [0, 0.05, 0.5], undefined, [1.05, 0.6, 1.35])
  a.p(sph(0.03, 5, 4), ventre, [0, 0.035, 0.57], undefined, [0.8, 0.6, 1.2])
  a.paire(() => sph(0.018, 5, 4), 0x1a1208, [0.036, 0.062, 0.51])
  return a.fin()
}

function geoLezard(): THREE.BufferGeometry {
  const ref = ANIMAUX.lezard.couleurs[0] as number
  const a = atelier(ref)
  const dos = 0x8c9a4a
  const ventre = 0xd8dca0
  const tache = 0x5f6b30
  a.p(sph(0.062, 8, 5), dos, [0, 0.05, 0], undefined, [1.1, 0.62, 1.9])
  a.p(sph(0.05, 6, 5), ventre, [0, 0.028, 0], undefined, [1, 0.32, 1.9])
  a.p(sph(0.05, 8, 6), dos, [0, 0.055, 0.15], undefined, [1, 0.72, 1.35])
  a.p(sph(0.026, 5, 4), dos, [0, 0.045, 0.21], undefined, [0.8, 0.7, 1.2])
  a.paire(() => sph(0.014, 5, 4), 0x14140c, [0.032, 0.07, 0.165])
  // Crete dorsale.
  for (let k = 0; k < 4; k++) {
    a.p(con(0.012, 0.03, 4), tache, [0, 0.1, 0.06 - k * 0.055])
  }
  // Queue effilee.
  for (let k = 0; k < 4; k++) {
    const t = k / 3
    a.p(sph(0.036 * (1 - t * 0.78), 6, 4), k % 2 ? tache : dos, [0, 0.035, -0.13 - t * 0.19])
  }
  // Quatre pattes ecartees, ventre au sol.
  for (const sz of [0.09, -0.07]) {
    a.paire(() => cyl(0.011, 0.009, 0.075, 4), dos, [0.055, 0.035, sz], [0, 0, 1.15])
    a.paire(() => boite(0.038, 0.008, 0.03), dos, [0.09, 0.006, sz])
  }
  return a.fin()
}

function geoTortue(): THREE.BufferGeometry {
  const ref = ANIMAUX.tortue.couleurs[0] as number
  const a = atelier(ref)
  const carapace = 0x6f7038
  const bord = 0xa2a54e
  const peau = 0xa8a870
  // Dome haut et bombe, cercle par une margelle etroite: vue de dessus, une
  // tortue doit se lire comme un dome a ecailles, pas comme une galette.
  a.p(sph(0.2, 11, 7), carapace, [0, 0.11, 0], undefined, [0.92, 0.95, 1.1])
  a.p(sph(0.21, 11, 4), bord, [0, 0.075, 0], undefined, [0.98, 0.1, 1.15])
  // Ecailles sur le dos: la carapace n'est pas une bille.
  const ecailles: [number, number][] = [
    [0, 0.15],
    [0, -0.02],
    [0, -0.17],
    [0.1, 0.06],
    [-0.1, 0.06],
    [0.1, -0.1],
    [-0.1, -0.1],
  ]
  for (const [ex, ez] of ecailles) {
    a.p(sph(0.062, 6, 4), bord, [ex, 0.265 - Math.abs(ex) * 0.5 - Math.abs(ez) * 0.35, ez], undefined, [1, 0.42, 1])
  }
  // Tete et cou tendus vers l'avant, bien hors de la carapace.
  a.p(cyl(0.045, 0.055, 0.12, 5), peau, [0, 0.1, 0.21], [1.2, 0, 0])
  a.p(sph(0.062, 8, 6), peau, [0, 0.115, 0.31], undefined, [0.95, 0.85, 1.2])
  a.p(sph(0.028, 5, 4), peau, [0, 0.1, 0.375], undefined, [0.8, 0.7, 1])
  a.paire(() => sph(0.017, 5, 4), 0x151208, [0.038, 0.14, 0.335])
  // Pattes trapues, en palettes.
  for (const sz of [0.11, -0.1]) {
    a.paire(() => cyl(0.042, 0.036, 0.09, 5), peau, [0.14, 0.045, sz], [0, 0, 0.45])
    a.paire(() => sph(0.055, 6, 4), peau, [0.175, 0.016, sz + 0.02], undefined, [1, 0.32, 1.25])
  }
  a.p(con(0.026, 0.075, 4), peau, [0, 0.1, -0.23], [-1.25, 0, 0])
  return a.fin()
}

function geoHerisson(): THREE.BufferGeometry {
  const ref = ANIMAUX.herisson.couleurs[0] as number
  const a = atelier(ref)
  const piquants = 0x5c4a34
  const peau = 0xd8c8ae
  const clair = 0xe6d8c2
  a.p(sph(0.15, 10, 7), piquants, [0, 0.11, -0.02], undefined, [1.05, 0.9, 1.15])
  // Une couronne d'epines: c'est ce qu'on voit d'abord, vu de dessus.
  for (let k = 0; k < 16; k++) {
    const ang = (k / 16) * Math.PI * 2
    const r = 0.5 + ((k * 7) % 5) * 0.1
    a.p(
      con(0.02, 0.075, 4),
      piquants,
      [Math.cos(ang) * 0.11 * r, 0.16 + (1 - r) * 0.05, Math.sin(ang) * 0.12 * r - 0.02],
      [Math.sin(ang) * -0.6, 0, Math.cos(ang) * 0.6],
    )
  }
  // Museau pointu, rose, tendu hors des piquants.
  a.p(sph(0.062, 7, 6), peau, [0, 0.075, 0.11], undefined, [0.95, 0.85, 1.1])
  a.p(con(0.035, 0.075, 5), peau, [0, 0.065, 0.18], [1.35, 0, 0])
  a.p(sph(0.016, 5, 4), 0x3a2a24, [0, 0.062, 0.215])
  a.paire(() => sph(0.014, 5, 4), 0x141010, [0.035, 0.1, 0.14])
  a.paire(() => sph(0.024, 5, 4), peau, [0.055, 0.115, 0.085], undefined, [0.5, 1, 0.8])
  for (const sz of [0.07, -0.07]) {
    a.paire(() => cyl(0.015, 0.012, 0.05, 4), clair, [0.075, 0.025, sz])
  }
  return a.fin()
}

function geoSinge(): THREE.BufferGeometry {
  const ref = ANIMAUX.singe.couleurs[0] as number
  const a = atelier(ref)
  const poil = 0x8a5a30
  const face = 0xd7a878
  a.p(sph(0.13, 9, 7), poil, [0, 0.3, 0], undefined, [0.95, 1.15, 0.85])
  a.p(sph(0.1, 7, 5), face, [0, 0.28, 0.07], undefined, [0.85, 1, 0.6])
  // Tete ronde, face claire, oreilles bien detachees.
  a.p(sph(0.105, 9, 7), poil, [0, 0.5, 0.03])
  a.p(sph(0.082, 8, 6), face, [0, 0.485, 0.075], undefined, [1, 1, 0.7])
  a.paire(() => sph(0.036, 6, 5), face, [0.1, 0.51, 0.01], undefined, [0.45, 1, 1])
  a.paire(() => sph(0.019, 5, 4), 0x141010, [0.036, 0.515, 0.115])
  a.p(sph(0.022, 5, 4), 0x3a2a20, [0, 0.475, 0.135], undefined, [1.3, 0.7, 0.7])
  // Bras longs, jambes flechies.
  a.paire(() => cyl(0.026, 0.022, 0.24, 5), poil, [0.13, 0.3, 0.03], [0, 0, 0.35])
  a.paire(() => sph(0.032, 5, 4), face, [0.175, 0.19, 0.05])
  a.paire(() => cyl(0.03, 0.025, 0.19, 5), poil, [0.075, 0.13, -0.01], [0.2, 0, 0.12])
  a.paire(() => sph(0.036, 5, 4), face, [0.085, 0.03, 0.03], undefined, [0.9, 0.5, 1.5])
  // Queue longue, enroulee vers le haut.
  for (let k = 0; k < 5; k++) {
    const t = k / 4
    a.p(
      sph(0.026 - t * 0.008, 6, 4),
      poil,
      [0, 0.26 + Math.sin(t * 1.9) * 0.22, -0.12 - t * 0.06 + Math.sin(t * 3.2) * 0.03],
    )
  }
  return a.fin()
}

function geoPapillon(): THREE.BufferGeometry {
  const ref = ANIMAUX.papillon.couleurs[0] as number
  const a = atelier(ref)
  const aile = 0xf2a0d0
  const bord = 0x8a4a70
  a.p(cyl(0.008, 0.012, 0.1, 5), 0x3a2a34, [0, 0, 0], [Math.PI / 2, 0, 0])
  a.p(sph(0.014, 5, 4), 0x3a2a34, [0, 0.004, 0.055])
  a.paire(() => cyl(0.002, 0.002, 0.05, 3), 0x3a2a34, [0.008, 0.02, 0.07], [-0.5, 0, 0.3])
  // Deux paires d'ailes, la grande devant, la petite derriere.
  a.paire(() => sph(0.085, 7, 4), aile, [0.075, 0.01, 0.03], [0, 0.25, 0.1], [1, 0.05, 0.72])
  a.paire(() => sph(0.055, 6, 4), aile, [0.055, 0.005, -0.055], [0, -0.3, 0.1], [1, 0.05, 0.7])
  a.paire(() => sph(0.03, 5, 4), bord, [0.13, 0.012, 0.055], [0, 0.3, 0.1], [1, 0.06, 0.8])
  return a.fin()
}

function geoLuciole(): THREE.BufferGeometry {
  const ref = ANIMAUX.luciole.couleurs[0] as number
  const a = atelier(ref)
  a.p(sph(0.03, 7, 6), 0xdcff8a, [0, 0, -0.01], undefined, [1, 0.9, 1.5])
  a.p(sph(0.05, 8, 6), 0xdcff8a, [0, 0, -0.05], undefined, [1, 1, 1.2])
  a.paire(() => sph(0.05, 5, 4), 0x9ab060, [0.035, 0.012, 0], [0, 0.3, 0.15], [1, 0.05, 0.5])
  return a.fin()
}

function geoManchot(): THREE.BufferGeometry {
  return geoOiseau({
    ref: ANIMAUX.manchot.couleurs[0] as number,
    taille: 1.5,
    corps: 0x2a2f38,
    ventre: 0xfbfcfe,
    dos: 0x272c34,
    bec: 0xd88a2a,
    becLong: 0.09,
    becForme: 'pointu',
    ailes: 'nageoires',
    aileL: 0.1,
    bout: 0x1d2028,
    disque: false,
    aigrettes: false,
    pattes: 0xd88a2a,
    patteH: 0.1,
    debout: 1,
    queueL: 0.12,
  })
}

// ------------------------------------------------------------------ fabriques

const FABRIQUES: Record<AnimalKind, () => THREE.BufferGeometry> = {
  lapin: () =>
    geoRongeur({
      ref: ANIMAUX.lapin.couleurs[0] as number,
      corps: 0xc8b49a,
      ventre: 0xf6f1e8,
      taille: 1.15,
      oreilleH: 0.24,
      oreillePointe: 0,
      queue: 'boule',
      pattes: 0xdccbb4,
      tache: 0x5a4a3c,
    }),
  lievre_arctique: () =>
    geoRongeur({
      ref: ANIMAUX.lievre_arctique.couleurs[0] as number,
      corps: 0xf1f5f9,
      ventre: 0xfdfeff,
      taille: 1.3,
      oreilleH: 0.19,
      oreillePointe: 1,
      queue: 'boule',
      pattes: 0xe4ebf2,
      tache: 0x2f3238,
    }),
  ecureuil: () =>
    geoRongeur({
      ref: ANIMAUX.ecureuil.couleurs[0] as number,
      corps: 0xc4763a,
      ventre: 0xffe7cf,
      taille: 0.78,
      oreilleH: 0.13,
      oreillePointe: 1,
      queue: 'panache',
      pattes: 0xa85f2c,
      tache: 0x7a4520,
    }),

  renard: () =>
    geoQuad({
      ref: ANIMAUX.renard.couleurs[0] as number,
      corpsL: 0.62,
      corpsH: 0.29,
      corpsR: 0.115,
      garrot: 0.015,
      couL: 0.11,
      couDroit: 0.35,
      teteR: 0.085,
      museauL: 0.095,
      oreille: 'pointue',
      cornes: 'aucune',
      queue: 'touffue',
      patteR: 0.03,
      bosse: 0,
      corps: 0xd86a22,
      ventre: 0xfff2e2,
      membres: 0x3a2a24,
      museau: 0x2a2018,
      motif: 'chaussettes',
      tache: 0x241a14,
    }),
  loup: () =>
    geoQuad({
      ref: ANIMAUX.loup.couleurs[0] as number,
      corpsL: 0.92,
      corpsH: 0.44,
      corpsR: 0.16,
      garrot: 0.035,
      couL: 0.15,
      couDroit: 0.3,
      teteR: 0.12,
      museauL: 0.12,
      oreille: 'pointue',
      cornes: 'aucune',
      queue: 'touffue',
      patteR: 0.042,
      bosse: 0.02,
      corps: 0x6f6a63,
      ventre: 0xdad6cf,
      membres: 0x8a857e,
      museau: 0x2a2724,
      motif: 'aucun',
      tache: 0x3f3a35,
    }),
  panthere: () =>
    geoQuad({
      ref: ANIMAUX.panthere.couleurs[0] as number,
      corpsL: 1.05,
      corpsH: 0.42,
      corpsR: 0.155,
      garrot: 0.02,
      couL: 0.13,
      couDroit: 0.12,
      teteR: 0.115,
      museauL: 0.085,
      oreille: 'ronde',
      cornes: 'aucune',
      queue: 'fine',
      patteR: 0.05,
      bosse: 0,
      corps: 0x33323a,
      ventre: 0x6a655e,
      membres: 0x2c2b33,
      museau: 0x18171c,
      motif: 'taches',
      tache: 0x17161b,
    }),
  ours: () =>
    geoQuad({
      ref: ANIMAUX.ours.couleurs[0] as number,
      corpsL: 1.35,
      corpsH: 0.62,
      corpsR: 0.29,
      garrot: 0.09,
      couL: 0.16,
      couDroit: 0.1,
      teteR: 0.19,
      museauL: 0.16,
      oreille: 'ronde',
      cornes: 'aucune',
      queue: 'courte',
      patteR: 0.085,
      bosse: 0.1,
      corps: 0xeef2f6,
      ventre: 0xf7f9fb,
      membres: 0xe2e8ee,
      museau: 0x2a2c30,
      motif: 'aucun',
      tache: 0xd8dee4,
    }),
  cerf: () =>
    geoQuad({
      ref: ANIMAUX.cerf.couleurs[0] as number,
      corpsL: 0.95,
      corpsH: 0.62,
      corpsR: 0.155,
      garrot: 0.05,
      couL: 0.26,
      couDroit: 0.72,
      teteR: 0.095,
      museauL: 0.1,
      oreille: 'longue',
      cornes: 'ramure',
      queue: 'courte',
      patteR: 0.032,
      bosse: 0,
      corps: 0xa8703a,
      ventre: 0xe4c49a,
      membres: 0x8f5f30,
      museau: 0x241c14,
      motif: 'taches',
      tache: 0xe4c49a,
    }),
  renne: () =>
    geoQuad({
      ref: ANIMAUX.renne.couleurs[0] as number,
      corpsL: 1.02,
      corpsH: 0.6,
      corpsR: 0.18,
      garrot: 0.055,
      couL: 0.24,
      couDroit: 0.55,
      teteR: 0.1,
      museauL: 0.11,
      oreille: 'longue',
      cornes: 'palmee',
      queue: 'courte',
      patteR: 0.04,
      bosse: 0,
      corps: 0x8f7a5c,
      ventre: 0xe8ddc8,
      membres: 0x6f5c44,
      museau: 0x2a2318,
      motif: 'aucun',
      tache: 0xd4c6ac,
    }),
  chevre: () =>
    geoQuad({
      ref: ANIMAUX.chevre.couleurs[0] as number,
      corpsL: 0.66,
      corpsH: 0.42,
      corpsR: 0.115,
      garrot: 0.025,
      couL: 0.15,
      couDroit: 0.5,
      teteR: 0.075,
      museauL: 0.075,
      oreille: 'longue',
      cornes: 'cornes',
      queue: 'courte',
      patteR: 0.026,
      bosse: 0,
      corps: 0xe6dcc8,
      ventre: 0xf6f2ea,
      membres: 0xbfb09a,
      museau: 0x2c2620,
      motif: 'aucun',
      tache: 0x8a7c68,
    }),
  sanglier: () =>
    geoQuad({
      ref: ANIMAUX.sanglier.couleurs[0] as number,
      corpsL: 0.85,
      corpsH: 0.35,
      corpsR: 0.185,
      garrot: 0.075,
      couL: 0.06,
      couDroit: 0.05,
      teteR: 0.115,
      museauL: 0.135,
      oreille: 'pointue',
      cornes: 'defenses',
      queue: 'fine',
      patteR: 0.038,
      bosse: 0.05,
      corps: 0x4a423c,
      ventre: 0x6f635c,
      membres: 0x2f2a26,
      museau: 0x8a7268,
      motif: 'soies',
      tache: 0x241f1c,
    }),
  chameau: () =>
    geoQuad({
      ref: ANIMAUX.chameau.couleurs[0] as number,
      corpsL: 1.1,
      corpsH: 0.72,
      corpsR: 0.2,
      garrot: 0.03,
      couL: 0.42,
      couDroit: 0.8,
      teteR: 0.1,
      museauL: 0.11,
      oreille: 'ronde',
      cornes: 'aucune',
      queue: 'fine',
      patteR: 0.042,
      bosse: 0.24,
      corps: 0xc5a271,
      ventre: 0xe6cca0,
      membres: 0xb08e5f,
      museau: 0x5f4c34,
      motif: 'aucun',
      tache: 0x8f7248,
    }),

  oiseau: () =>
    geoOiseau({
      ref: ANIMAUX.oiseau.couleurs[0] as number,
      taille: 1,
      corps: 0xd8523a,
      ventre: 0xfbe9c8,
      dos: 0xc44a34,
      bec: 0xe0a02a,
      becLong: 0.075,
      becForme: 'pointu',
      ailes: 'repliees',
      aileL: 0.1,
      bout: 0x6f2c20,
      disque: false,
      aigrettes: false,
      pattes: 0xe0a02a,
      patteH: 0.055,
      debout: 0.25,
      queueL: 0.16,
    }),
  mouette: () =>
    geoOiseau({
      ref: ANIMAUX.mouette.couleurs[0] as number,
      taille: 1.5,
      corps: 0xffffff,
      ventre: 0xffffff,
      dos: 0xd0dae2,
      bec: 0xe8b02a,
      becLong: 0.1,
      becForme: 'pointu',
      ailes: 'deployees',
      aileL: 0.17,
      bout: 0x2c3038,
      disque: false,
      aigrettes: false,
      pattes: 0xe8b02a,
      patteH: 0.05,
      debout: 0.12,
      queueL: 0.15,
    }),
  aigle: () =>
    geoOiseau({
      ref: ANIMAUX.aigle.couleurs[0] as number,
      taille: 2.2,
      corps: 0x4a3524,
      ventre: 0x6a4c30,
      dos: 0x3f2c1e,
      bec: 0xf0c040,
      becLong: 0.1,
      becForme: 'crochu',
      ailes: 'deployees',
      aileL: 0.22,
      bout: 0x241a12,
      disque: false,
      aigrettes: false,
      pattes: 0xf0c040,
      patteH: 0,
      debout: 0,
      queueL: 0.2,
    }),
  hibou: () =>
    geoOiseau({
      ref: ANIMAUX.hibou.couleurs[0] as number,
      taille: 1.5,
      corps: 0x8a6f48,
      ventre: 0xe0cca8,
      dos: 0x7a5c3a,
      bec: 0x3a2c1c,
      becLong: 0.05,
      becForme: 'crochu',
      ailes: 'repliees',
      aileL: 0.13,
      bout: 0x4a3720,
      disque: true,
      aigrettes: true,
      pattes: 0xa88a5c,
      patteH: 0.05,
      debout: 0.55,
      queueL: 0.12,
    }),
  canard: () =>
    geoOiseau({
      ref: ANIMAUX.canard.couleurs[0] as number,
      taille: 1.35,
      corps: 0x8a7c62,
      ventre: 0xf6efe0,
      dos: 0x6f6248,
      bec: 0xe0a02a,
      becLong: 0.11,
      becForme: 'plat',
      ailes: 'repliees',
      aileL: 0.12,
      bout: 0x2f5a4a,
      disque: false,
      aigrettes: false,
      pattes: 0xe0a02a,
      patteH: 0,
      debout: 0.18,
      queueL: 0.1,
    }),
  manchot: geoManchot,

  dauphin: () =>
    geoNageur({
      ref: ANIMAUX.dauphin.couleurs[0] as number,
      longueur: 1.5,
      rayon: 0.18,
      dos: 0x8199aa,
      ventre: 0xdfe9f2,
      caudale: 'horizontale',
      dorsale: 0.09,
      rostre: 0.2,
      ronde: false,
      rayures: 0,
      tache: 0x2f3a44,
    }),
  poisson_mer: () =>
    geoNageur({
      ref: ANIMAUX.poisson_mer.couleurs[0] as number,
      longueur: 0.3,
      rayon: 0.055,
      dos: 0x3a8aa0,
      ventre: 0xdff0f4,
      caudale: 'verticale',
      dorsale: 0.028,
      rostre: 0,
      ronde: false,
      rayures: 3,
      tache: 0x1e4a5c,
    }),
  phoque: () =>
    geoNageur({
      ref: ANIMAUX.phoque.couleurs[0] as number,
      longueur: 1.05,
      rayon: 0.19,
      dos: 0x7f8a94,
      ventre: 0xd8dee4,
      caudale: 'horizontale',
      dorsale: 0,
      rostre: 0,
      ronde: true,
      rayures: 0,
      tache: 0x4a5259,
    }),

  crabe: geoCrabe,
  lezard: geoLezard,
  serpent: geoSerpent,
  scorpion: geoScorpion,
  tortue: geoTortue,
  herisson: geoHerisson,
  singe: geoSinge,
  papillon: geoPapillon,
  luciole: geoLuciole,
}

// ------------------------------------------------------------------ classe

interface EspeceRendu {
  kind: AnimalKind
  maillage: THREE.InstancedMesh
  demarche: Demarche
  entrain: number
  /** Rayon englobant, pour le tri par champ de vision et l'anneau. */
  rayon: number
  /** Nombre d'instances effectivement utilisees. */
  utilisees: number
}

export class RenduAnimaux {
  readonly groupe: THREE.Group
  private scene: THREE.Scene
  private especes = new Map<AnimalKind, EspeceRendu>()
  private materiau: THREE.MeshLambertMaterial
  private materiauLumineux: THREE.MeshBasicMaterial
  private anneau: THREE.Mesh
  private anneauVisible = false
  private temps = 0
  private matrice = new THREE.Matrix4()
  private quat = new THREE.Quaternion()
  private quatTmp = new THREE.Quaternion()
  private pos = new THREE.Vector3()
  private echelle = new THREE.Vector3()
  private couleur = new THREE.Color()
  private axeY = new THREE.Vector3(0, 1, 0)
  private axeX = new THREE.Vector3(1, 0, 0)
  private axeZ = new THREE.Vector3(0, 0, 1)
  private camera: THREE.Camera | null = null
  private frustum = new THREE.Frustum()
  private matVue = new THREE.Matrix4()
  private sphere = new THREE.Sphere()
  /** Position precedente par animal (index = id), pour deduire la vitesse. */
  private precX = new Float32Array(0)
  private precZ = new Float32Array(0)
  private connu = new Uint8Array(0)
  /** Nombre d'animaux ecrits a la derniere image. Pour les mesures. */
  private ecrits = 0

  constructor(scene: THREE.Scene, animaux: Animal[], camera?: THREE.Camera) {
    this.scene = scene
    this.camera = camera ?? null
    this.groupe = new THREE.Group()
    this.groupe.name = 'animaux'
    scene.add(this.groupe)
    this.materiau = new THREE.MeshLambertMaterial({ vertexColors: true })
    // La luciole ne recoit pas la lumiere: elle en produit. Un materiau non
    // eclaire suffit a la faire briller dans la nuit sans post-traitement.
    this.materiauLumineux = new THREE.MeshBasicMaterial({ vertexColors: true })

    const especes = new Set<AnimalKind>()
    for (const a of animaux) especes.add(a.kind)

    for (const kind of especes) {
      const fabrique = FABRIQUES[kind]
      if (!fabrique) continue
      const geo = fabrique()
      const mat = kind === 'luciole' ? this.materiauLumineux : this.materiau
      const maillage = new THREE.InstancedMesh(geo, mat, MAX_PAR_ESPECE)
      maillage.name = `animal:${kind}`
      maillage.castShadow = PORTE_OMBRE.includes(kind)
      maillage.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
      maillage.count = 0
      /*
       * three.js ne calcule la sphere englobante d'un InstancedMesh qu'UNE
       * fois, a la premiere image, a partir des positions du moment. Or les
       * animaux errent en permanence: un individu qui sortait de la zone
       * initiale de son espece pouvait etre elimine a tort et disparaitre de
       * l'ecran (et de la passe d'ombre). On pose donc une sphere qui couvre
       * tout le monde jouable, et on la fige. Le tri fin, lui, se fait
       * instance par instance dans `maj`.
       *
       * ELLE COUVRE LA CARTE, PAS L'ILE 1. Centree sur (250, 250) avec 375 m de
       * rayon, elle laissait l'ile 2 a treize cents metres dehors: le jour ou
       * des betes y vivent, l'elimination se fait AVANT de regarder les
       * instances, donc c'est toute l'espece qui disparaissait d'un coup — et de
       * la passe d'ombre avec. Le facteur 0,53 reproduit exactement la marge
       * d'avant (0,75 de cote pour une demi-diagonale de 0,707).
       */
      maillage.boundingSphere = new THREE.Sphere(
        new THREE.Vector3(CARTE_X / 2, 20, CARTE_Z / 2),
        Math.hypot(CARTE_X, CARTE_Z) * 0.53,
      )
      maillage.computeBoundingSphere = (): void => {}
      this.groupe.add(maillage)
      const p = PROFILS[kind]
      this.especes.set(kind, {
        kind,
        maillage,
        demarche: p.demarche,
        entrain: p.entrain,
        rayon: geo.boundingSphere ? geo.boundingSphere.radius : 0.5,
        utilisees: 0,
      })
    }

    this.anneau = new THREE.Mesh(
      new THREE.TorusGeometry(0.42, 0.05, 7, 20),
      new THREE.MeshBasicMaterial({ color: 0x9ef0b0, transparent: true, opacity: 0.85 }),
    )
    this.anneau.rotation.x = -Math.PI / 2
    this.anneau.visible = false
    this.groupe.add(this.anneau)
  }

  /**
   * Donne la camera au rendu: les animaux hors du champ ne sont alors ni
   * animes ni dessines. A appeler une fois par image (comme `RenduFeux`), ou
   * une seule fois si la camera est un objet stable.
   */
  majCamera(camera: THREE.Camera): void {
    this.camera = camera
  }

  /**
   * Ecrit les matrices d'instance depuis l'etat de la simulation.
   * A appeler une fois par frame, apres `majAnimaux`.
   */
  maj(animaux: Animal[], dt: number): void {
    this.temps += dt
    for (const e of this.especes.values()) e.utilisees = 0
    this.ecrits = 0

    if (animaux.length > this.precX.length) {
      const n = animaux.length + 32
      const nx = new Float32Array(n)
      const nz = new Float32Array(n)
      const nc = new Uint8Array(n)
      nx.set(this.precX)
      nz.set(this.precZ)
      nc.set(this.connu)
      this.precX = nx
      this.precZ = nz
      this.connu = nc
    }

    const cam = this.camera
    if (cam) {
      this.matVue.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse)
      this.frustum.setFromProjectionMatrix(this.matVue)
    }

    for (const a of animaux) {
      const e = this.especes.get(a.kind)
      if (!e || e.utilisees >= MAX_PAR_ESPECE) continue

      // Tri par champ de vision: hors de l'ecran, on n'ecrit rien du tout.
      if (cam) {
        this.sphere.center.set(a.pos.x, a.y, a.pos.z)
        this.sphere.radius = e.rayon * a.taille + MARGE_CHAMP
        if (!this.frustum.intersectsSphere(this.sphere)) continue
      }

      const i = e.utilisees++
      this.ecrits++

      const id = a.id
      const vitesse =
        this.connu[id] === 1
          ? Math.hypot(a.pos.x - (this.precX[id] as number), a.pos.z - (this.precZ[id] as number)) /
            Math.max(dt, 1e-4)
          : 0
      this.precX[id] = a.pos.x
      this.precZ[id] = a.pos.z
      this.connu[id] = 1

      const bouge = clamp(vitesse / 2.5, 0, 1)
      const c = this.temps * (6 + bouge * 7) + a.phase * Math.PI * 2
      const t = this.temps
      const ph = a.phase
      const dort = a.etat === 'dort'
      const jeu = a.etat === 'troupeau' && a.cible !== null

      let y = a.y
      let tangage = 0
      let roulis = 0
      let lacet = 0
      let ex = 1
      let ey = 1
      let ez = 1

      if (dort) {
        // Endormi: pose au sol, respiration lente, un peu affaisse.
        y += Math.sin(t * 1.4 + ph * 6) * 0.012
        tangage = 0.12
        ey = 0.92
        if (a.kind === 'luciole') {
          // Le jour, la luciole est eteinte: on ne la dessine pas du tout.
          ex = 0
          ey = 0
          ez = 0
        }
      } else if (a.etat === 'boule') {
        // Herisson roule en boule, tortue rentree dans sa carapace.
        ex = 1.18
        ey = 0.72
        ez = 1.05
        y -= 0.02
        lacet = Math.sin(t * 3) * 0.04
      } else if (a.etat === 'garde') {
        // Il tient sa place: il gratte le sol du sabot et souffle.
        tangage = 0.09 + Math.sin(t * 7 + ph) * 0.055
        lacet = Math.sin(t * 2.3 + ph) * 0.07
        y += Math.abs(Math.sin(t * 7 + ph)) * 0.012
      } else {
        switch (e.demarche) {
          case 'sautille': {
            const saut = Math.max(0, Math.sin(c))
            y += saut * (0.12 + bouge * 0.24)
            tangage = -saut * 0.42 * (0.35 + bouge)
            ey = 1 - saut * 0.06
            ez = 1 + saut * 0.05
            break
          }
          case 'trotte': {
            y += Math.abs(Math.sin(c * 0.72)) * 0.055 * bouge
            roulis = Math.sin(c * 0.72) * 0.055 * bouge
            tangage = Math.sin(c * 1.44) * 0.05 * bouge
            break
          }
          case 'marche': {
            y += Math.abs(Math.sin(c * 0.6)) * 0.028 * bouge
            roulis = Math.sin(c * 0.6) * 0.07 * bouge
            lacet = Math.sin(c * 0.6) * 0.05 * bouge
            tangage = Math.sin(c * 1.2) * 0.025 * bouge
            break
          }
          case 'lourd': {
            roulis = Math.sin(c * 0.45) * 0.14 * bouge
            y += Math.abs(Math.sin(c * 0.45)) * 0.03 * bouge
            lacet = Math.sin(c * 0.45) * 0.08 * bouge
            break
          }
          case 'feline': {
            // Foulee basse: le dos ondule, l'animal reste pres du sol.
            y += Math.abs(Math.sin(c * 0.55)) * 0.02 * bouge
            roulis = Math.sin(c * 0.55) * 0.035 * bouge
            tangage = -0.035 + Math.sin(c * 1.1) * 0.03 * bouge
            ez = 1 + Math.sin(c * 1.1) * 0.03 * bouge
            break
          }
          case 'rampe': {
            roulis = Math.sin(c) * 0.1 * bouge
            lacet = Math.sin(c) * 0.2 * bouge
            y += Math.abs(Math.sin(c)) * 0.012 * bouge
            break
          }
          case 'ondule': {
            // Le serpent ondule meme presque arrete, et son corps se comprime.
            const amp = 0.22 + bouge * 0.3
            lacet = Math.sin(c * 0.9) * amp
            ex = 1 + Math.sin(c * 0.9) * 0.1
            ez = 1 - Math.sin(c * 0.9) * 0.06
            roulis = Math.sin(c * 0.9 + 1.2) * 0.07
            break
          }
          case 'saccade': {
            // Salves: pas de balancement, un frisson et un arret net.
            lacet = Math.sin(c * 2.4) * 0.07 * bouge
            y += Math.abs(Math.sin(c * 2.4)) * 0.008 * bouge
            tangage = -0.02 * bouge
            break
          }
          case 'lateral': {
            // Le crabe avance DE COTE: on tourne le modele d'un quart de tour.
            lacet = Math.PI / 2 + Math.sin(c * 0.9) * 0.12 * bouge
            roulis = Math.sin(c * 0.9) * 0.16 * bouge
            y += Math.abs(Math.sin(c * 1.8)) * 0.018 * bouge
            break
          }
          case 'dandine': {
            roulis = Math.sin(c * 0.55) * (0.06 + 0.2 * bouge)
            y += Math.abs(Math.sin(c * 0.55)) * 0.022 * bouge
            tangage = Math.sin(c * 1.1) * 0.03 * bouge
            break
          }
          case 'traine': {
            // A-coups: elle avance, s'arrete, repart.
            const pas = Math.max(0, Math.sin(c * 0.35))
            y += pas * 0.012 * bouge
            tangage = Math.sin(c * 0.7) * 0.05 * bouge
            roulis = Math.sin(c * 0.35) * 0.07 * bouge
            break
          }
          case 'vol_bat': {
            // Le battement se lit sur la largeur: les ailes s'ouvrent et se
            // ferment. Plus le vol est rapide, plus le battement est ample.
            const f = Math.sin(c * 1.8)
            ex = 1 + f * (0.14 + bouge * 0.16)
            ey = 1 - f * 0.06
            y += Math.sin(c * 0.9) * 0.12
            roulis = Math.sin(c * 0.45) * 0.22 * (0.35 + bouge)
            tangage = -bouge * 0.16
            break
          }
          case 'vol_plane': {
            // Ailes fixes, inclinaison constante dans le virage, longue houle.
            const sens = (a.id & 1) === 0 ? 1 : -1
            roulis = sens * 0.3 + Math.sin(t * 0.5 + ph) * 0.05
            y += Math.sin(t * 0.35 + ph) * 0.35
            ex = 1 + Math.sin(t * 0.6 + ph) * 0.03
            tangage = -0.06
            break
          }
          case 'vol_erratique': {
            const f = Math.sin(c * 3.4)
            ex = 1 + f * 0.42
            ez = 1 - Math.abs(f) * 0.12
            y += Math.sin(c * 1.1) * 0.16 + Math.sin(c * 0.47 + 1.3) * 0.1
            lacet = Math.sin(c * 0.6) * 0.35
            roulis = f * 0.18
            break
          }
          case 'nage': {
            lacet = Math.sin(c * 1.1) * (0.12 + bouge * 0.16)
            roulis = Math.sin(c * 0.55) * 0.12
            y += Math.sin(c * 0.55) * 0.05
            ez = 1 + Math.sin(c * 1.1) * 0.04
            break
          }
          case 'bond_eau': {
            // Le dauphin sort de l'eau en arc, plusieurs fois par minute.
            const u = Math.sin(t * 0.62 + ph * 2.1)
            const arc = u > 0 ? Math.pow(u, 6) : 0
            y += arc * 1.35
            tangage = -Math.cos(t * 0.62 + ph * 2.1) * Math.sqrt(arc) * 1.15
            lacet = Math.sin(c * 0.9) * 0.1
            roulis = Math.sin(c * 0.5) * 0.14 + arc * 0.4
            break
          }
          case 'flotte': {
            // Il se laisse porter par la houle, et plonge la tete de temps en temps.
            y += Math.sin(t * 1.1 + ph) * 0.035
            roulis = Math.sin(t * 0.85 + ph * 1.7) * 0.09
            const u = Math.sin(t * 0.3 + ph * 1.9)
            const plongee = u > 0.93 ? (u - 0.93) / 0.07 : 0
            tangage = plongee * 1.15
            y -= plongee * 0.05
            lacet = Math.sin(t * 0.6 + ph) * 0.12
            break
          }
        }

        // Le jeu se voit: on bondit sur place, on se cabre, on remue.
        if (jeu && e.entrain > 0) {
          const k = e.entrain
          y += Math.abs(Math.sin(c * 0.8)) * 0.09 * k
          tangage -= Math.max(0, Math.sin(c * 0.4)) * 0.3 * k
          lacet += Math.sin(c * 1.6) * 0.16 * k
          roulis += Math.sin(c * 0.55) * 0.12 * k
        }
      }

      this.pos.set(a.pos.x, y, a.pos.z)
      this.quat.setFromAxisAngle(this.axeY, a.rot + lacet)
      if (tangage !== 0) {
        this.quatTmp.setFromAxisAngle(this.axeX, tangage)
        this.quat.multiply(this.quatTmp)
      }
      if (roulis !== 0) {
        this.quatTmp.setFromAxisAngle(this.axeZ, roulis)
        this.quat.multiply(this.quatTmp)
      }
      // Un souffle d'echelle pour la respiration: discret mais vivant.
      const souffle = 1 + Math.sin(t * 2.2 + ph * 5) * 0.018
      const s = a.taille * souffle
      this.echelle.set(ex * s, ey * s, ez * s)

      this.matrice.compose(this.pos, this.quat, this.echelle)
      e.maillage.setMatrixAt(i, this.matrice)
      this.couleur.setHex(a.couleur)
      e.maillage.setColorAt(i, this.couleur)
    }

    for (const e of this.especes.values()) {
      e.maillage.count = e.utilisees
      e.maillage.instanceMatrix.needsUpdate = true
      if (e.maillage.instanceColor) e.maillage.instanceColor.needsUpdate = true
    }

    if (this.anneauVisible) {
      const mt = this.anneau.material as THREE.MeshBasicMaterial
      mt.opacity = 0.6 + Math.sin(this.temps * 6) * 0.25
      this.anneau.rotation.z += dt * 1.2
    }
  }

  /** Anneau autour de l'animal vise, pour proposer de le nourrir. */
  surbriller(a: Animal | null): void {
    if (!a) {
      this.anneau.visible = false
      this.anneauVisible = false
      return
    }
    const e = this.especes.get(a.kind)
    const rayon = (e ? e.rayon : 0.5) * a.taille
    this.anneau.position.set(a.pos.x, a.y + 0.05, a.pos.z)
    this.anneau.scale.setScalar(clamp(rayon * 1.9, 0.7, 4))
    this.anneau.visible = true
    this.anneauVisible = true
  }

  nombreEspeces(): number {
    return this.especes.size
  }

  /** Mesures de budget: especes dessinees, instances ecrites, triangles. */
  mesures(): { especesDessinees: number; instances: number; triangles: number; appels: number } {
    let especesDessinees = 0
    let triangles = 0
    let appels = 0
    for (const e of this.especes.values()) {
      if (e.utilisees === 0) continue
      especesDessinees++
      appels += 1 + (e.maillage.castShadow ? 1 : 0)
      const pos = e.maillage.geometry.getAttribute('position')
      triangles += (pos.count / 3) * e.utilisees
    }
    return { especesDessinees, instances: this.ecrits, triangles, appels }
  }

  dispose(): void {
    for (const e of this.especes.values()) {
      e.maillage.geometry.dispose()
      e.maillage.dispose()
    }
    this.materiau.dispose()
    this.materiauLumineux.dispose()
    this.anneau.geometry.dispose()
    ;(this.anneau.material as THREE.Material).dispose()
    this.especes.clear()
    this.scene.remove(this.groupe)
  }
}
